import type { CMSStory, CmsStoryContent, CmsCopy } from "../adapters";
import { type ApiConfiguration } from "./shared";

const getTranslatedSlug = (slug: string, config: ApiConfiguration) => {
  if (config.locale === "en-CH") return slug;

  const arr = slug.split("/");
  arr.shift();
  return arr.join("/");
};

export const localeCopyApi = (config: ApiConfiguration) => ({
  async getLocaleCopy(slugs: string[]): Promise<Record<string, string>> {
    const response = await config.cmsService.getAll<{
      Text?: string;
      Title?: string;
      DisplayedName?: string;
      SubText?:
        | {
            content?: { content?: { text?: string }[] }[];
          }
        | string;
      BoxTitle?: string;
      ContentExample?: string;
      MainText?: string;
      TooltipText?: string;
      singular?: string;
      plural?: string;
    }>("", slugs, { by_slugs: slugs.map(e => e + "/*").join(",") });

    return response.reduce((result: Record<string, string>, obj) => {
      const slug = getTranslatedSlug(obj.full_slug, config);
      if (obj.content.Text) {
        result[slug] = obj.content.Text;
      }
      if (obj.content.DisplayedName) {
        result[slug] = obj.content.DisplayedName;
      }
      if (obj.content.Title) {
        result[`${slug}/title`] = obj.content.Title;
      }
      if (obj.content.SubText) {
        if (typeof obj.content.SubText === "string") {
          result[`${slug}/sub-text`] = obj.content.SubText;
        } else if (obj.content.SubText.content?.[0].content?.[0].text) {
          result[`${slug}/sub-text`] = obj.content.SubText.content?.[0].content?.[0].text;
        }
      }
      if (obj.content.BoxTitle) {
        result[`${slug}/box-title`] = obj.content.BoxTitle;
      }
      if (obj.content.ContentExample) {
        result[`${slug}/content-example`] = obj.content.ContentExample;
      }
      if (obj.content.MainText) {
        result[`${slug}/main-text`] = obj.content.MainText;
      }
      if (obj.content.TooltipText) {
        result[`${slug}/tooltip-text`] = obj.content.TooltipText;
      }
      return result;
    }, {});
  },
  async getCmsCopy(storiesPrefixes: string[]): Promise<CmsCopy> {
    const response = await config.cmsService.getAll<CmsStoryContent>("", storiesPrefixes, {
      by_slugs: storiesPrefixes.map(e => e + "/*").join(","),
    });

    const processLocaleCopy = (response: CMSStory<CmsStoryContent>[]): Record<string, string> =>
      response.reduce((result: Record<string, string>, obj) => {
        const slug = getTranslatedSlug(obj.full_slug, config);
        const textProperties: { [key: string]: string | undefined } = {
          [slug]: obj.content.Text ?? obj.content.DisplayedName,
          [`${slug}/title`]: obj.content.Title,
          [`${slug}/sub-text`]:
            typeof obj.content.SubText === "string"
              ? obj.content.SubText
              : obj.content.SubText?.content?.[0]?.content?.[0]?.text,
          [`${slug}/box-title`]: obj.content.BoxTitle,
          [`${slug}/content-example`]: obj.content.ContentExample,
          [`${slug}/main-text`]: obj.content.MainText,
          [`${slug}/tooltip-text`]: obj.content.TooltipText,
          [`${slug}/maintenances`]: JSON.stringify(obj.content.Maintenances?.at(-1)),
        };

        Object.entries(textProperties).forEach(([key, value]) => {
          if (value) result[key] = value;
        });

        return result;
      }, {});

    const processOtherStories = (response: CMSStory<CmsStoryContent>[]): Record<string, CmsStoryContent> => {
      return response.reduce((result: Record<string, CmsStoryContent>, obj) => {
        const slug = getTranslatedSlug(obj.full_slug, config);
        const identifier = obj.full_slug.split("/").pop() || "";
        result[slug] = { ...obj.content, slug: identifier };
        return result;
      }, {});
    };

    const cmsCopy = {
      localeCopy: processLocaleCopy(response),
      otherStories: processOtherStories(response),
    };

    return cmsCopy;
  },
});
